import {
  Button,
  Select,
  SelectOption,
  TextInput,
} from "@wearewqa/wqa-design-system";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Modal } from "../components/Modal/Modal";
import { getStores } from "../resources/stores";
import { createVersion } from "../resources/versions";
import { Heading } from "../components/Heading";
import { FileInput } from "../components/FileInput";
import { Spinner } from "../components";

type CreateVersionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appId: string;
};

type CreateVersionFormData = {
  version_number: string;
  build_number: string;
  display_name: string | null;
  store_id: string;
  app_id: string;
};

export const CreateVersionModal = ({
  appId,
  isOpen,
  onClose,
}: CreateVersionModalProps) => {
  const [stores, setStores] = useState<SelectOption[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<CreateVersionFormData>({
    version_number: "",
    build_number: "",
    display_name: null,
    store_id: "",
    app_id: appId,
  });

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  }

  function onInputChange(key: string, value: string) {
    setFormData((prevState) => ({ ...prevState, [key]: value }));
  }

  async function manageFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    if (!file) {
      throw new Error("File is missing. Please add a file.");
    }

    try {
      const version = await createVersion(formData, file);
      if (version) {
        console.log(
          `Success. Version '${version.display_name || version.version_number}' has been created.`
        );
        window.location.reload();
      }
      setIsLoading(false);
    } catch (error) {
      console.error("AdminVersionForm - ManageFormSubmit: ", error);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    async function buildData() {
      try {
        const storesData = await getStores();

        const storeSelectOptions: SelectOption[] = storesData.body.map(
          (current) => ({
            label: current.name,
            value: current.id as string,
          })
        );

        setStores(storeSelectOptions);
      } catch (error) {
        console.error("AdminVersionForm - buildData: ", error);
      }
    }

    void buildData();
  }, []);

  return (
    <Modal {...{ isOpen, onClose }}>
      <div className="create-version-modal">
        <Heading type="h2" label="Upload a new Version." />
        <br />
        <form className="modal-form" onSubmit={(e) => void manageFormSubmit(e)}>
          {stores !== null && (
            <>
              <div className="modal-form__store-inputs">
                <Select
                  required
                  id={"version_form__stores"}
                  label={"Select an store."}
                  options={stores}
                  value={formData.store_id}
                  onChange={(e) => onInputChange("store_id", e)}
                />
              </div>

              <TextInput
                required
                id={"version_form__version_name"}
                label={"Version Number"}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onInputChange("version_number", event.target.value)
                }
              />

              <TextInput
                required
                id={"version_form__build_number"}
                label={"Build Number"}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onInputChange("build_number", event.target.value)
                }
              />

              <TextInput
                id={"version_form__display_name"}
                label={"Display Name"}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onInputChange("display_name", event.target.value)
                }
              />

              <FileInput
                id={"version_form__file"}
                label={"Upload Version file."}
                onChange={onFileChange}
              />

              {!isLoading ? (
                <Button label={"Submit"} type="submit" />
              ) : (
                <Spinner />
              )}
            </>
          )}
        </form>
      </div>
    </Modal>
  );
};
